import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { API_BASE } from './config';
import { getToken, getRefreshToken, setAccessToken, setRefreshToken, clearAuth } from './auth';
import { getDeviceId } from './device';

let onSessionExpired: (() => void) | null = null;
export function registerSessionExpiredHandler(fn: () => void) {
  onSessionExpired = fn;
}

export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use(async (config) => {
  config.headers = config.headers || {};
  const token = await getToken();
  if (token) {
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  try {
    const deviceId = await getDeviceId();
    if (deviceId) (config.headers as any)['X-Device-Id'] = deviceId;
  } catch {
    /* ignore */
  }
  return config;
});

let refreshing: Promise<string | null> | null = null;

async function doRefresh(): Promise<string | null> {
  const rt = await getRefreshToken();
  if (!rt) return null;
  try {
    const deviceId = await getDeviceId().catch(() => null);
    const headers: Record<string, string> = {};
    if (deviceId) headers['X-Device-Id'] = deviceId;
    const res = await axios.post(`${API_BASE}/auth/refresh`, { refresh_token: rt }, { headers });
    const access = res.data.access_token as string;
    const newRt = res.data.refresh_token as string;
    await setAccessToken(access);
    if (newRt) await setRefreshToken(newRt);
    return access;
  } catch {
    return null;
  }
}

api.interceptors.response.use(
  r => r,
  async (error: AxiosError) => {
    const original = error.config as AxiosRequestConfig & { _retry?: boolean };
    if (error.response?.status === 401 && !original?._retry) {
      original._retry = true;
      if (!refreshing) refreshing = doRefresh().finally(() => { refreshing = null; });
      const newToken = await refreshing;
      if (newToken) {
        original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newToken}` };
        return api(original);
      }
      await clearAuth();
      onSessionExpired?.();
    }
    return Promise.reject(error);
  },
);
